import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { isAuthConfigured } from './auth-config';

export interface AuthContextValue {
  /** True when an Auth0 tenant is wired in. The Sign-in button should hide otherwise. */
  isConfigured: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { sub?: string; name?: string; email?: string } | undefined;
  /** Throws if not authenticated. */
  getToken: () => Promise<string>;
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * Exported so alternate providers (e.g. the e2e token-injection seam in
 * `E2eAuthProvider`) can supply the same context that `useAuth` reads.
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const configured = isAuthConfigured();
  const auth0 = useAuth0();

  const getToken = useCallback(async (): Promise<string> => {
    if (!configured) {
      throw new Error('Auth not configured');
    }
    if (!auth0.isAuthenticated) {
      throw new Error('Not authenticated');
    }
    return auth0.getAccessTokenSilently();
  }, [configured, auth0]);

  const login = useCallback(
    async (returnTo?: string): Promise<void> => {
      if (!configured) {
        throw new Error('Auth not configured');
      }
      await auth0.loginWithRedirect(returnTo ? { appState: { returnTo } } : {});
    },
    [configured, auth0],
  );

  const logout = useCallback(async (): Promise<void> => {
    if (!configured) return;
    await auth0.logout({ logoutParams: { returnTo: window.location.origin } });
  }, [configured, auth0]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isConfigured: configured,
      isAuthenticated: configured ? auth0.isAuthenticated : false,
      isLoading: configured ? auth0.isLoading : false,
      user: configured ? auth0.user : undefined,
      getToken,
      login,
      logout,
    }),
    [configured, auth0.isAuthenticated, auth0.isLoading, auth0.user, getToken, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthContextProvider');
  }
  return ctx;
}
